import {Minus, Plus} from 'lucide-react';
import * as React from 'react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {IconButton} from '@/components/ui/icon-button';
import type {InputProps} from '@/components/ui/input';
import {Input} from '@/components/ui/input';
import {cn} from '@/utils/ui';

export type QuantityInputProps = Omit<
  InputProps,
  'value' | 'onChange' | 'min' | 'max' | 'type'
> & {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const QuantityInput = React.forwardRef<HTMLInputElement, QuantityInputProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = Number.MAX_SAFE_INTEGER,
      className,
      disabled,
      onBlur,
      ...inputProps
    },
    ref
  ) => {
    const {t} = useTranslation();

    // The input keeps its own draft while typing, so an intermediate value such
    // as '' or '-' stays editable. It resyncs whenever the committed value changes.
    const [inputValue, setInputValue] = useState<string>(String(value));
    const [committedValue, setCommittedValue] = useState<number>(value);

    if (value !== committedValue) {
      setCommittedValue(value);
      setInputValue(String(value));
    }

    const commitValue = (newValue: number) => {
      const clamped = clamp(newValue, min, max);

      setInputValue(String(clamped));
      onChange(clamped);
    };

    const syncDraftValue: React.ChangeEventHandler<
      HTMLInputElement
    > = event => {
      const draft = event.target.value;

      setInputValue(draft);

      const parsed = Number(draft);
      const isUsable =
        draft.trim() !== '' &&
        !Number.isNaN(parsed) &&
        parsed >= min &&
        parsed <= max;

      // Only a draft that is already within range goes out on every keystroke,
      // so consumers get live feedback without ever seeing a value they would
      // have to undo. Anything else waits for blur to clamp it. Marking it
      // committed keeps the resync above from rewriting drafts such as '04'.
      if (!isUsable) return;

      setCommittedValue(parsed);
      onChange(parsed);
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = event => {
      const parsed = Number(inputValue);

      commitValue(
        inputValue.trim() === '' || Number.isNaN(parsed) ? 0 : parsed
      );
      onBlur?.(event);
    };

    return (
      <div className="flex w-full flex-row items-center gap-2">
        <IconButton
          onClick={() => commitValue(value - 1)}
          variant="secondary"
          type="button"
          disabled={disabled || value <= min}
        >
          <Minus />
          <span className="sr-only">{t('Minus')}</span>
        </IconButton>

        <Input
          type="number"
          className={cn('min-w-0 grow text-center', className)}
          value={inputValue}
          onChange={syncDraftValue}
          ref={ref}
          disabled={disabled}
          {...inputProps}
          onBlur={handleBlur}
        />

        <IconButton
          onClick={() => commitValue(value + 1)}
          variant="secondary"
          type="button"
          disabled={disabled || value >= max}
        >
          <Plus />
          <span className="sr-only">{t('Plus')}</span>
        </IconButton>
      </div>
    );
  }
);

QuantityInput.displayName = 'QuantityInput';

export {QuantityInput};
